import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/authStore'
import authRoutes from '@/router/common/authRoutes'
import accountRoutes from '@/router/common/accountRoutes'
import profileRoutes from '@/router/common/profileRoutes'
import roleRoutes from '@/router/common/roleRoutes'
import restrictedRoutes from '@/router/restrictedRoutes'
import distributorNetworkRoutes from '@/router/referential/distributor/distributorNetworkRoutes'
import keyAccountNetworkRoutes from '@/router/referential/distributor/keyAccountNetworkRoutes'
import keyAccountGroupRoutes from '@/router/referential/distributor/keyAccountGroupRoutes'
import distributorGroupRoutes from '@/router/referential/distributor/distributorGroupRoutes'
import storeRoutes from '@/router/referential/distributor/storeRoutes'
import operatingCenterRoutes from '@/router/referential/distributor/operatingCenterRoutes'
import billingRoutes from '@/router/referential/distributor/billingRoutes'
import manufacturerRoutes from '@/router/referential/product/manufacturerRoutes'
import categoryRoutes from '@/router/referential/product/categoryRoutes'
import subCategoryRoutes from '@/router/referential/product/subCategoryRoutes'
import productRoutes from '@/router/referential/product/productRoutes'
import guaranteeRoutes from '@/router/referential/product/guaranteeRoutes'
import otherRoutes from '@/router/referential/other/otherRoutes'
import importExportRoutes from '@/router/referential/import-export/importExportRoutes'
import repairerRoutes from '@/router/referential/repairer/repairerRoutes'
import transporterRoutes from '@/router/referential/transporter/transporterRoutes'
import repairFileRoutes from '@/router/repair-files/repairFileRoutes'
import rentalFileRoutes from '@/router/rental-files/rentalFileRoutes'
import rentalProductRoutes from '@/router/rental-products/rentalProductRoutes'
import recyclerRoutes from '@/router/recycling-file-module/recycler-management/recyclerRoutes'
import recyclerCenterRoutes from '@/router/recycling-file-module/recycler-management/recyclerCenterRoutes'
import recyclerCenterLinkRoutes from '@/router/recycling-file-module/recycler-management/recyclerCenterLinkRoutes'
import reconditionerRoutes from '@/router/recycling-file-module/reconditioner-management/reconditionerRoutes'
import reconditionerCenterRoutes from '@/router/recycling-file-module/reconditioner-management/reconditionerCenterRoutes'
import reconditionerCenterLinkRoutes from '@/router/recycling-file-module/reconditioner-management/reconditionerCenterLinkRoutes'
import recyclingRoutes from '@/router/recycling-file-module/recyclingRoutes'
import keyAccountProductRoutes from '@/router/key-account-products/keyAccountProductRoutes'
import regionCodeRoutes from '@/router/referential/region-code/regionCodeRoutes'
import emailOutboxRoutes from '@/router/emailOutboxRoutes'
import createLegacyRoutes from '@/router/legacyRoutes'
import { displayToast } from '@/utils/toastUtils'
import i18n from '@/plugins/i18n/config.js'
import { checkPermission } from '@/utils/permissionUtils'

const { t } = i18n.global

const routes = [
  ...restrictedRoutes,
  ...authRoutes,
  ...accountRoutes,
  ...profileRoutes,
  ...roleRoutes,
  ...distributorNetworkRoutes,
  ...keyAccountNetworkRoutes,
  ...keyAccountGroupRoutes,
  ...distributorGroupRoutes,
  ...storeRoutes,
  ...operatingCenterRoutes,
  ...billingRoutes,
  ...manufacturerRoutes,
  ...categoryRoutes,
  ...subCategoryRoutes,
  ...productRoutes,
  ...guaranteeRoutes,
  ...importExportRoutes,
  ...otherRoutes,
  ...repairerRoutes,
  ...transporterRoutes,
  ...repairFileRoutes,
  ...rentalFileRoutes,
  ...rentalProductRoutes,
  ...recyclingRoutes,
  ...keyAccountProductRoutes,
  ...recyclerRoutes,
  ...recyclerCenterRoutes,
  ...recyclerCenterLinkRoutes,
  ...reconditionerRoutes,
  ...reconditionerCenterRoutes,
  ...reconditionerCenterLinkRoutes,
  ...regionCodeRoutes,
  ...emailOutboxRoutes
]

const checkRoutePermissions = (route) => {
  if (!route.meta?.permissions) {
    return true
  }

  return checkPermission(route.meta.permissions)
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

createLegacyRoutes(router).forEach((route) => router.addRoute(route))

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const isLoggedIn = authStore.auth.status.loggedIn
  const requiresAuth = to.meta?.requiresAuth

  if (to.meta?.publicCapability) {
    return next()
  }

  if (!isLoggedIn) {
    if (requiresAuth) {
      return next('/')
    }
    return next()
  }

  if (isLoggedIn) {
    if (requiresAuth && from.meta?.publicCapability) {
      await authStore.me()
      if (!authStore.auth.status.loggedIn) return next('/')
    }
    if (!requiresAuth && to.path === '/') {
      return next({ name: 'repairFiles' })
    }

    if (requiresAuth && !checkRoutePermissions(to)) {
      await authStore.me()

      if (!checkRoutePermissions(to)) {
        displayToast(t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'error')
        return next('/not-authorized')
      }
    }
  }

  next()
})

router.onError((error) => {
  if (error?.message?.includes('Failed to fetch dynamically imported module')) {
    const alreadyReloaded = sessionStorage.getItem('chunk-reload')

    if (!alreadyReloaded) {
      sessionStorage.setItem('chunk-reload', 'true')
      window.location.reload()
    }
  }
})

export default router
